/**
 * InterviewFlow logged-in home — solutions umbrella + tenant Studio workspace.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccountTree as FlowIcon,
  Assignment as FormIcon,
  AutoAwesome as MCPIcon,
  Chat as ChatIcon,
  Edit as EditIcon,
  Hub as HubIcon,
  People as PeopleIcon,
  Assignment as PermitIcon,
  LocalShipping as ReturnsIcon,
  BusinessCenter as CrmIcon,
  ViewModule as AppIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FullscreenButton from '../FullscreenButton';
import { listV2Flows, listV2Apps } from '../../api/studioV2.api';
import { listFormDesigns } from '../../api/formBuilder.api';
import { listStudioRecents } from '../../utils/studioRecents';
import { fetchTenantHomePath, DEFAULT_TENANT_HOME } from '../../utils/resolveTenantHome';

const DISPLAY = '"Fraunces", Georgia, serif';
const BODY = '"Source Sans 3", "Source Sans Pro", system-ui, sans-serif';
const ACCENT = '#1976d2';

const SOLUTIONS = [
  {
    id: 'hiring',
    name: 'Hiring',
    tagline: 'Screen candidates and run scorecards.',
    path: '/studio/templates',
    icon: PeopleIcon,
  },
  {
    id: 'permits',
    name: 'Permits',
    tagline: 'SF citizen advisor — path, fees, zoning.',
    path: '/v2/demo/permit',
    icon: PermitIcon,
  },
  {
    id: 'crm',
    name: 'CRM',
    tagline: 'Qualify leads and manage the pipeline.',
    path: '/crm',
    icon: CrmIcon,
  },
  {
    id: 'returns',
    name: 'Returns',
    tagline: 'Guided return and RMA paths.',
    path: '/demo',
    icon: ReturnsIcon,
  },
];

const BUILD = [
  { label: 'Studio', path: '/studio', icon: HubIcon, desc: 'Flows, apps, forms' },
  { label: 'Flows', path: '/studio/flows', icon: FlowIcon, desc: 'Flow editor' },
  { label: 'Apps', path: '/studio/apps', icon: AppIcon, desc: 'App builder' },
  { label: 'Chat', path: '/home', icon: ChatIcon, desc: 'Try your flows' },
  { label: 'MCP', path: '/mcp-dashboard', icon: MCPIcon, desc: 'Tools' },
];

function recentHref(item) {
  if (item.href) return item.href;
  if (item.kind === 'flow') return `/studio/flows/${item.id}`;
  if (item.kind === 'app') return `/studio/apps/${item.id}`;
  if (item.kind === 'form') return '/studio/forms';
  return '/studio';
}

function recentKindIcon(kind) {
  if (kind === 'app') return AppIcon;
  if (kind === 'form') return FormIcon;
  return FlowIcon;
}

function recentKindLabel(kind) {
  if (kind === 'app') return 'App';
  if (kind === 'form') return 'Form';
  return 'Flow';
}

export default function InterviewFlowDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const ink = isDark ? '#e8eef6' : '#0c1a2e';

  const [stats, setStats] = useState({ totalForms: 0, totalFlows: 0, totalApps: 0 });
  const [recentItems, setRecentItems] = useState([]);
  const [loadingWorkspace, setLoadingWorkspace] = useState(true);
  const [configuredHomePath, setConfiguredHomePath] = useState(null);

  const orderedRecents = useMemo(() => [...recentItems].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return Number(b.t || 0) - Number(a.t || 0);
  }), [recentItems]);

  const loadWorkspace = async () => {
    setLoadingWorkspace(true);
    try {
      const [{ flows }, { apps }, { forms }] = await Promise.all([
        listV2Flows(),
        listV2Apps(),
        listFormDesigns(),
      ]);
      setStats({
        totalFlows: flows?.length || 0,
        totalApps: apps?.length || 0,
        totalForms: forms?.length || 0,
      });
      setRecentItems(listStudioRecents());
    } catch (e) {
      if (import.meta.env.DEV) console.error(e);
    } finally {
      setLoadingWorkspace(false);
    }
  };

  useEffect(() => {
    loadWorkspace();
    (async () => {
      const path = await fetchTenantHomePath();
      if (path && path !== DEFAULT_TENANT_HOME) {
        setConfiguredHomePath(path);
      }
    })();
  }, []);

  const cardSx = {
    border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(12, 26, 46, 0.1)',
    borderRadius: 2,
    bgcolor: isDark ? 'rgba(30,30,30,0.85)' : 'rgba(255, 255, 255, 0.92)',
    p: 2.25,
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        fontFamily: BODY,
        color: ink,
        p: { xs: 2, md: 3 },
        background: isDark
          ? 'linear-gradient(180deg, #0d1117 0%, #161b22 100%)'
          : 'radial-gradient(900px 420px at 8% 0%, rgba(25, 118, 210, 0.14), transparent 55%), linear-gradient(180deg, #f4f7fb 0%, #eef3f8 100%)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2 }}>
        <Box>
          <Typography
            component="h1"
            data-testid="if-home-title"
            sx={{
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.15rem' },
              letterSpacing: '-0.02em',
              mb: 0.75,
            }}
          >
            Welcome to InterviewFlow
          </Typography>
          <Typography sx={{ opacity: 0.75, maxWidth: 540, lineHeight: 1.5 }}>
            Guided interviews for hiring, permits, CRM, and customer work. Open a solution, or build in Studio.
          </Typography>
          {configuredHomePath && (
            <Button
              data-testid="if-home-open-configured"
              variant="contained"
              size="small"
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700 }}
              onClick={() => navigate(configuredHomePath)}
            >
              Open your home app
            </Button>
          )}
        </Box>
        <FullscreenButton />
      </Box>

      <Typography
        component="h2"
        sx={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.02em', mb: 1.5 }}
      >
        Solutions
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
          gap: 1.75,
          mb: 4,
        }}
      >
        {SOLUTIONS.map(({
          id, name, tagline, path, icon: Icon,
        }) => (
          <Box
            key={id}
            component="button"
            type="button"
            data-testid={`if-home-solution-${id}`}
            onClick={() => navigate(path)}
            sx={{
              textAlign: 'left',
              cursor: 'pointer',
              ...cardSx,
              fontFamily: BODY,
              color: ink,
              transition: 'transform 0.2s ease, border-color 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                borderColor: ACCENT,
              },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                bgcolor: ACCENT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.25,
              }}
            >
              <Icon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Typography sx={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: '1.1rem', mb: 0.5 }}>{name}</Typography>
            <Typography sx={{ fontSize: '0.9rem', opacity: 0.7, lineHeight: 1.45 }}>{tagline}</Typography>
          </Box>
        ))}
      </Box>

      <Typography
        component="h2"
        sx={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.02em', mb: 1.5 }}
      >
        Build
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mb: 4 }}>
        {BUILD.map(({
          label, path, icon: Icon, desc,
        }) => (
          <Button
            key={path}
            onClick={() => navigate(path)}
            data-testid={`if-home-build-${label.toLowerCase()}`}
            startIcon={<Icon />}
            variant="outlined"
            sx={{
              fontFamily: BODY,
              fontWeight: 600,
              textTransform: 'none',
              justifyContent: 'flex-start',
              borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(12, 26, 46, 0.16)',
              color: ink,
              bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255, 255, 255, 0.7)',
              px: 2,
              py: 1.25,
              minWidth: { sm: 180 },
              '&:hover': { borderColor: ACCENT },
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.75rem', opacity: 0.55, fontWeight: 400 }}>{desc}</Typography>
            </Box>
          </Button>
        ))}
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
        <Typography
          component="h2"
          data-testid="if-home-workspace"
          sx={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.02em' }}
        >
          Your workspace
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => loadWorkspace()}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Refresh
        </Button>
      </Box>

      {loadingWorkspace ? (
        <Box sx={{ mb: 3 }}>
          <LinearProgress sx={{ borderRadius: 1 }} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' },
              gap: 1.5,
              mb: 2.5,
            }}
          >
            {[
              { label: 'Flows', value: stats.totalFlows, path: '/studio/flows', icon: FlowIcon },
              { label: 'Apps', value: stats.totalApps, path: '/studio/apps', icon: AppIcon },
              { label: 'Forms', value: stats.totalForms, path: '/studio/forms', icon: FormIcon },
            ].map(({
              label, value, path, icon: Icon,
            }) => (
              <Box
                key={label}
                component="button"
                type="button"
                data-testid={`if-home-stat-${label.toLowerCase()}`}
                onClick={() => navigate(path)}
                sx={{
                  ...cardSx,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: BODY,
                  color: ink,
                  border: 'none',
                  outline: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(12, 26, 46, 0.1)'}`,
                  '&:hover': { outlineColor: ACCENT },
                }}
              >
                <Icon sx={{ color: ACCENT, mb: 0.75 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.1 }}>{value}</Typography>
                <Typography sx={{ fontSize: '0.85rem', opacity: 0.65 }}>{label}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ ...cardSx }} data-testid="if-home-recents">
            <Typography sx={{ fontFamily: DISPLAY, fontWeight: 600, mb: 1 }}>Recent in Studio</Typography>
            {orderedRecents.length === 0 ? (
              <Typography sx={{ fontSize: '0.9rem', opacity: 0.65 }}>
                No recent Studio items yet. Open a flow or app in Studio to populate this list.
              </Typography>
            ) : (
              <List dense disablePadding>
                {orderedRecents.slice(0, 8).map((item) => {
                  const KindIcon = recentKindIcon(item.kind);
                  const href = recentHref(item);
                  return (
                    <ListItem
                      key={`${item.kind}_${item.id}`}
                      disablePadding
                      secondaryAction={(
                        <Button size="small" onClick={() => navigate(href)} aria-label="Open" sx={{ minWidth: 36 }}>
                          <EditIcon fontSize="small" />
                        </Button>
                      )}
                    >
                      <ListItemButton onClick={() => navigate(href)} sx={{ borderRadius: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <KindIcon sx={{ fontSize: 18, color: ACCENT }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name || item.id}
                          secondary={recentKindLabel(item.kind)}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
